<template>
  <v-app>
    <v-navigation-drawer
      v-if="$route.name !== 'Login'"
      v-model="drawer"
      color="primary"
      width="230"
      class="v-nav"
      disable-resize-watcher
      disable-route-watcher
      dark
      app
    >
      <v-list nav>
        <div v-for="item in navItems" :key="item.title" class="mb-2">
          <v-list-item :to="item.to" link>
            <v-list-item-icon>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title class="nav-menu-text">{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </div>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar v-if="$route.name !== 'Login'" app flat color="white" class="v-header" height="64">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-app-bar-nav-icon @click="drawer = !drawer" v-on="on"> </v-app-bar-nav-icon>
        </template>
        <span>{{ $t('menu') }}</span>
      </v-tooltip>
      <v-toolbar-title class="pl-1">
        <router-link :to="toHome">
          <v-img src="@/assets/kobelink_logo.png" contain height="50" width="50"> </v-img>
        </router-link>
      </v-toolbar-title>
      <!-- 環境変数情報 -->
      <v-toolbar-title v-if="envTitle !== 'production'" class="ml-4 text-h4 env-title">
        {{ envTitle }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <!-- メンテナンス通知 -->
      <MaintenanceNotifications
        :userId="user_info.userId"
        v-if="user_info.userId"
        @reloadRouterView="reloadRouterView"
      />
      <!-- お知らせ情報 -->
      <Info v-if="access_token" />
      <!-- 言語切替メニュー -->
      <v-menu offset-y bottom min-width="100">
        <template v-slot:activator="{ on: langMenuOn, attrs }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: langSlotOn }">
              <v-btn v-on="{ ...langMenuOn, ...langSlotOn }" v-bind="attrs" icon>
                <v-icon>mdi-web</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('languageSwitching') }}</span>
          </v-tooltip>
        </template>
        <v-list dense>
          <v-subheader class="font-weight-bold text-center">
            {{ $t('languageSwitching') }}
          </v-subheader>
          <v-divider></v-divider>
          <v-list-item-group v-model="selectedLanguage" color="primary">
            <v-list-item @click="changeLocale(language.id)" v-for="language in languages" :key="language.id">
              <v-list-item-title>{{ language.text }}</v-list-item-title>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
      <!-- ヘルプ -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            class="mr-3"
            href="https://www.kobelink.com/root/documents/kobelink_help.html"
            target="_blank"
            v-on="on"
          >
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('help') }}</span>
      </v-tooltip>
      <!-- ログイン情報 -->
      <v-menu max-width="500" offset-y bottom rounded>
        <template v-slot:activator="{ on: userMenuOn, attrs }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: userSlotOn }">
              <v-avatar
                v-on="{ ...userMenuOn, ...userSlotOn }"
                v-bind="attrs"
                slot="activator"
                size="45"
                color="blue darken-1"
                class="mr-5"
              >
                <custom-icon type="account" :colors="['#1565C0', '#FFFFFF']"> </custom-icon>
              </v-avatar>
            </template>
            <span>{{ $t('loginInformation') }}</span>
          </v-tooltip>
        </template>
        <!-- ログインアカウント情報 -->
        <v-list width="200" dense>
          <v-list-item class="text-center">
            <v-list-item-content>
              <v-list-item-title>
                <v-avatar size="45">
                  <custom-icon type="account" :colors="['#1565C0', '#FFFFFF']"> </custom-icon>
                </v-avatar>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <template v-if="user_info.userId && user_info.userName">
            <v-list-item class="text-center">
              <v-list-item-content>
                <v-list-item-title>
                  {{ user_info.userId }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item class="text-center">
              <v-list-item-content>
                <v-list-item-title>
                  {{ user_info.userName }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <v-divider></v-divider>
          <!-- ログアウト -->
          <v-list-item class="text-center mt-2">
            <v-list-item-content>
              <v-btn @click="logOut" color="primary" depressed class="flex-grow-1 flex-shrink-0">
                {{ $t('logOut') }}
              </v-btn>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="text-center">
            <v-list-item-content>
              <v-btn
                href="https://account.activedirectory.windowsazure.com/ChangePassword.aspx"
                target="_blank"
                color="primary"
                depressed
                class="flex-grow-1 flex-shrink-0"
              >
                {{ $t('changePassword') }}
              </v-btn>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-toolbar-title v-if="$vuetify.breakpoint.smAndUp">
        <v-img src="@/assets/kobelco_logo.png" contain height="20" width="80"> </v-img>
      </v-toolbar-title>
    </v-app-bar>

    <!-- 処理中ローディング表示 -->
    <v-overlay :z-index="999" :value="loading">
      <v-progress-circular :size="80" :width="6" color="primary" indeterminate> </v-progress-circular>
    </v-overlay>

    <!-- スナックバー -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      :multi-line="snackbar.multiline"
      top
      app
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn @click="snackbar.show = false" v-bind="attrs" text dark> Close </v-btn>
      </template>
    </v-snackbar>

    <!-- コンテンツ領域 -->
    <v-main>
      <v-toolbar color="blue darken-3" class="v-fixed-toolbar" flat dark dense>
        <span v-if="String($route.meta.icon).startsWith('mdi')" class="mr-2">
          <v-icon>{{ $route.meta.icon }}</v-icon>
        </span>
        <span v-else class="mr-2 pt-2">
          <custom-icon v-if="$route.meta.icon" :type="$route.meta.icon" :size="25" :colors="$route.meta.colors">
          </custom-icon>
        </span>
        <v-toolbar-title class="font-weight-bold">
          {{ $t($route.meta.title) }}
        </v-toolbar-title>
      </v-toolbar>
      <!-- 子コンポーネント領域 -->
      <v-fade-transition>
        <router-view v-if="($route.name === 'Login' || access_token) && reload" />
      </v-fade-transition>
    </v-main>
  </v-app>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { msalMixin } from './plugins/msal';
  import CustomIcon from './components/CustomIcon';
  import Info from '@/components/Info.vue';
  import MaintenanceNotifications from '@/components/MaintenanceNotifications.vue';

  export default {
    name: 'App',
    components: {
      CustomIcon,
      Info,
      MaintenanceNotifications,
    },
    mixins: [msalMixin],
    data: () => ({
      drawer: false,
      languages: [
        {
          id: 'en',
          text: 'English',
        },
        {
          id: 'ja',
          text: '日本語',
        },
      ],
      selectedLanguage: 0,
      reload: true,
      envTitle: process.env.NODE_ENV,
    }),
    created() {
      // 別タブ遷移時にqueryで言語設定を引き継ぐ
      if (this.$route.query.locale) {
        this.$i18n.locale = this.$route.query.locale;
        this.$moment.locale(this.$route.query.locale === 'zh' ? 'zh-cn' : this.$route.query.locale);
        this.$dayjs.locale(this.$route.query.locale === 'zh' ? 'zh-cn' : this.$route.query.locale);
      }
      // 言語切替メニューで現在の言語設定を強調表示するようにする
      this.selectedLanguage = (() => {
        switch (this.$i18n.locale) {
          case 'en':
            return 0;
          case 'ja':
            return 1;
          case 'zh':
            return 2;
        }
      })();
    },
    computed: {
      ...mapGetters('loading', ['loading']),
      ...mapGetters('snackbar', ['snackbar']),
      ...mapGetters('auth', ['access_token', 'user_info']),
      navItems() {
        if ('Q118800Admin@user.kobelink.com' === this.user_info.userId) {
          return [
            {
              title: this.$t('manageNotifications'),
              icon: 'mdi-cog',
              to: '/InfoSetting',
            },
            {
              title: this.$t('logging'),
              icon: 'mdi-file-find-outline',
              to: '/Logs',
            },
          ];
        } else if (!this.user_info.admin) {
          return [
            {
              title: this.$t('customerSMachineNo'),
              icon: 'mdi-cog',
              to: `/MachineNumberSetting`,
            },
            {
              title: this.$t('machineGroupSettings'),
              icon: 'mdi-cog',
              to: `/GroupingSetting`,
            },
            {
              title: this.$t('alarmNotificationEmailSettings'),
              icon: 'mdi-cog',
              to: {
                name: `AlermMailSetting`,
                params: {
                  device_group_id: this.user_info.deviceGroupId,
                  group_id: undefined,
                  device_group_name_locale: undefined,
                  model_id: undefined,
                  serial_no: undefined,
                },
              },
            },
          ];
        }
        return [];
      },
      toHome() {
        if (this.user_info.admin) {
          return {
            name: 'HomeAdmin',
            params: {
              device_group_id: this.user_info.deviceGroupId,
            },
          };
        } else {
          return {
            name: 'HomeConsumer',
            params: {
              device_group_id: this.user_info.deviceGroupId,
            },
          };
        }
      },
    },
    watch: {
      $route: {
        async handler(to) {
          // Login画面への遷移の場合は処理しない
          if (to.name !== 'Login') {
            await this.getAccessToken();
            await this.getUserInfo();
            // ルートに遷移する場合は、遷移先を条件に合わせて設定する
            if (to.name === 'Root') {
              // if (this.user_info.isMap) {
              // this.$router.push({
              //     name: 'DashboardAdmin',
              //     params: {
              //       device_group_id: this.user_info.deviceGroupId,
              //     },
              //   });
              // } else {
              this.$router.push(this.toHome);
              // }
            }
          }
        },
        immediate: true,
      },
      // オフライン時の警告メッセージ表示
      isOffline() {
        if (this.isOffline) {
          this.$store.commit('snackbar/show_snackbar', {
            text: 'ネットワーク接続がオフラインです',
            color: 'error',
            timeout: 5000,
            multiline: false,
          });
        } else {
          this.$store.commit('snackbar/hide_snackbar');
        }
      },
    },
    methods: {
      ...mapActions('auth', ['get_access_token', 'get_user_info']),
      async getAccessToken() {
        if (!this.access_token) {
          if (process.env.NODE_ENV === 'production') {
            await this.get_access_token({ msal: this.$msal });
          } else {
            // localやdev環境でaccess_tokenが取得できない場合はログイン画面に遷移する
            this.$router.push('/Login');
          }
        }
      },
      async getUserInfo() {
        if (!this.user_info.deviceGroupId) {
          if (process.env.NODE_ENV === 'production') {
            await this.get_user_info(this.$msal.data.user);
          } else {
            await this.get_user_info({ username: this.user_info.userId, name: this.user_info.userName });
          }
        }
      },
      logOut() {
        this.$store.commit('auth/clear_state');
        this.$store.commit('groupAuthorities/clear_state');
        // ローカルストレージを破棄する
        localStorage.removeItem('vuex');
        if (process.env.NODE_ENV === 'production') {
          if (this.$msal.isAuthenticated()) {
            this.$store.commit('loading/start_loading');
            this.$msal.signOut();
            this.$store.commit('loading/end_loading');
          }
        } else {
          this.$router.push('/Login');
        }
      },
      changeLocale(languageId) {
        this.$i18n.locale = languageId;
        this.$moment.locale(languageId === 'zh' ? 'zh-cn' : languageId);
        this.$dayjs.locale(languageId === 'zh' ? 'zh-cn' : languageId);
        this.reloadRouterView();
      },
      reloadRouterView() {
        this.reload = false;
        this.$nextTick(() => {
          this.reload = true;
        });
      },
    },
  };
</script>
<style lang="scss">
  /* navigation-drawer展開時にheaderが右にずれないようにする */
  .v-header {
    left: 0 !important;
    right: 0 !important;
  }
  /* navigation-drawer展開時にheaderを除くようにする */
  .v-nav {
    margin-top: 64px !important;
    border-top-left-radius: 0px !important;
    border-top-right-radius: 0px !important;
  }
  /* サブヘッダーをfixed表示にする */
  .v-fixed-toolbar {
    position: fixed !important;
    z-index: 5 !important;
    width: 100% !important;
  }
  /* サブヘッダーの高さ分コンテンツ領域を下げる */
  .container {
    margin-top: 48px;
  }
  /* テーブルのセル内で折り返しを禁止 */
  .v-data-table td {
    white-space: nowrap !important;
  }
  .v-data-table th {
    white-space: nowrap !important;
  }
  /* 行のborderスタイルを当てる設定 */
  .v-data-table table {
    border-collapse: collapse;
  }
  /* テーブル内の最後の行の下部に線を表示する */
  .v-data-table table tbody tr:last-child {
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
  }
  /* ナビゲーションバーのメニューのテキスト */
  .nav-menu-text {
    white-space: normal !important;
  }
  /* データテーブルの基本デザイン */
  .v-data-table-outlined {
    /* テーブルの外枠を設定 */
    border: thin solid rgba(0, 0, 0, 0.12);
    border-radius: 0 !important;

    /* PC表示のヘッダは水色 */
    .v-data-table-header:not(.v-data-table-header-mobile) th {
      background-color: #d1e1f1 !important;
    }

    /* モバイル表示のヘッダは白 */
    .v-data-table-header-mobile {
      background-color: #ffffff;
    }

    /* PC表示のヘッダを意図的に白にしたい場合使用 */
    .v-data-table-background-white {
      background-color: #ffffff;
    }

    /* PC表示のヘッダ・セルの左側に境界線を設定 */
    .v-data-table-border-left {
      border-left: thin solid rgba(0, 0, 0, 0.12);
    }
  }
  // 実行環境のタイトルのスタイル
  .env-title {
    color: red;
  }
  .v-card-border-bottom {
    border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
  }
  .border-bottom-none {
    border-bottom: none;
  }
</style>
